
import React, { useState } from 'react';
import { ItemDraft } from '../types';
import { Plus, Trash2, ArrowRight, Image as ImageIcon, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { apiClient } from '../services/apiClient';
import { asArray } from '../utils/asArray';

interface ItemFormProps {
  brandNames: string[];
  items: ItemDraft[];
  setItems: React.Dispatch<React.SetStateAction<ItemDraft[]>>;
  onBack: () => void;
  onNext: () => void;
  currencyCode?: string;
  hideNav?: boolean;
}

export const ItemForm: React.FC<ItemFormProps> = ({ 
  brandNames, 
  items, 
  setItems, 
  onBack, 
  onNext,
  currencyCode,
  hideNav = false
}) => {
  const currency = currencyCode || 'QAR';
  const safeItems = asArray(items);
  const safeBrandNames = asArray(brandNames);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(safeItems[0]?.id || null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleAddItem = () => {
    const newItem: ItemDraft = {
      id: Date.now().toString(),
      name: '',
      originalPrice: '0', // Default to 0, hidden
      sellingPrice: '',
      description: '',
      image: null,
      imageName: undefined,
      selectedAggregators: [],
      managerNote: ''
    };
    const newItems = [...safeItems, newItem];
    setItems(newItems);
    setExpandedId(newItem.id);
    // Scroll to bottom
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
  };

  const handleRemoveItem = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (safeItems.length === 1) {
      alert("You must submit at least one item.");
      return;
    }
    const newItems = safeItems.filter(item => item.id !== id);
    setItems(newItems);
    if (expandedId === id) {
      setExpandedId(newItems[newItems.length - 1]?.id || null);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const updateItem = (id: string, field: keyof ItemDraft, value: any) => {
    setItems(prev => asArray(prev).map(item => item.id === id ? { ...item, [field]: value } : item));
    if (errors[`${id}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${id}-${field}`];
      setErrors(newErrors);
    }
  };

  const handleFileChange = async (itemId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    setUploadingId(itemId);
    setUploadError(null);

    try {
      // Preview immediately with a local object URL
      const localPreview = URL.createObjectURL(file);
      setItems(prev => asArray(prev).map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            imagePreview: localPreview
          };
        }
        return item;
      }));

      const res = await apiClient.uploads.uploadImage(file);
      const url = (res as any).url || (res as any).key;
      if (!url) throw new Error('No URL returned');
      setItems(prev => asArray(prev).map(item => {
        if (item.id === itemId) {
           return { 
             ...item, 
             image: url,
             imagePreview: localPreview,
             imageName: file.name
           };
        }
        return item;
      }));
    } catch (err) {
      console.warn('R2 upload failed, falling back to base64', err);
      setUploadError('Upload failed, using local image only.');
      const reader = new FileReader();
      reader.onloadend = () => {
        setItems(prev => asArray(prev).map(item => {
          if (item.id === itemId) {
             return { 
               ...item, 
               image: reader.result as string,
               imagePreview: reader.result as string,
               imageName: file.name
             };
          }
          return item;
        }));
      };
      reader.readAsDataURL(file);
    } finally {
      setUploadingId(null);
    }
  };

  const validateAndProceed = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    items.forEach(item => {
      if (!item.name.trim()) {
        newErrors[`${item.id}-name`] = 'Required';
        isValid = false;
      }
      if (!item.sellingPrice || isNaN(parseFloat(item.sellingPrice)) || parseFloat(item.sellingPrice) <= 0) {
        newErrors[`${item.id}-sellingPrice`] = 'Required';
        isValid = false;
      }
    });

    setErrors(newErrors);

    if (isValid) {
      onNext();
    } else {
      const firstErrorKey = Object.keys(newErrors)[0];
      if (firstErrorKey) {
        const errorItemId = firstErrorKey.split('-')[0];
        setExpandedId(errorItemId);
      }
    }
  };

  return (
    <div className="w-full pb-24 text-sm">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Add Items</h2>
        <p className="text-zinc-500 font-medium mt-1 text-sm">
            For <span className="text-teal-600 font-semibold">{brandNames.join(', ')}</span>
        </p>
      </div>

      <div className="space-y-4">
        {safeItems.map((item, index) => {
          const isExpanded = expandedId === item.id;
          const hasError = Object.keys(errors).some(k => k.startsWith(item.id));
          
          return (
            <div 
                key={item.id} 
                className={`
                    group bg-white rounded-2xl transition-all duration-300 overflow-hidden
                    ${isExpanded ? 'shadow-md ring-1 ring-zinc-200' : 'shadow-sm hover:shadow-md border border-transparent hover:border-zinc-200 cursor-pointer'}
                    ${hasError && !isExpanded ? 'ring-2 ring-red-100 bg-red-50/50' : ''}
                `}
                onClick={!isExpanded ? () => toggleExpand(item.id) : undefined}
            >
              {/* Collapsed Header / Preview */}
              {!isExpanded && (
                  <div className="p-4 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center text-zinc-400 font-bold text-xs">
                             #{index + 1}
                          </div>
                          <div>
                              <h3 className={`font-bold text-base ${item.name ? 'text-zinc-900' : 'text-zinc-400'}`}>
                                  {item.name || 'New Item'}
                              </h3>
                              <div className="flex gap-3 text-xs text-zinc-500 font-medium">
                                  <span className="text-teal-600 font-semibold">Price: {item.sellingPrice ? `${currency} ${item.sellingPrice}` : '-'}</span>
                              </div>
                          </div>
                      </div>
                      <div className="p-2 bg-zinc-50 rounded-full text-zinc-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                          <ChevronDown className="w-5 h-5" />
                      </div>
                  </div>
              )}
              
              {/* Expanded Form */}
              {isExpanded && (
                <div className="animate-fade-in">
                  <div className="px-6 py-5 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
                     <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Item Details #{index + 1}</span>
                     {safeItems.length > 1 && (
                        <button 
                            onClick={(e) => handleRemoveItem(item.id, e)}
                            className="text-zinc-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-xl transition-all"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                     )}
                  </div>

                  <div className="p-6 md:p-8 space-y-6">
                    
                    {/* Item Name */}
              <div className="relative group/input">
                <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                        placeholder=" "
                        className={`
                                peer w-full bg-zinc-100/50 hover:bg-zinc-100 border-0 rounded-xl px-4 pt-5 pb-2 text-zinc-900 font-semibold text-base focus:ring-2 focus:ring-teal-500/20 focus:bg-white transition-all outline-none
                                ${errors[`${item.id}-name`] ? 'ring-2 ring-red-100 bg-red-50' : ''}
                            `}
                     />
                     <label className="absolute left-4 top-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:text-zinc-500 peer-focus:top-2.5 peer-focus:text-[10px] peer-focus:text-teal-500 transition-all pointer-events-none">
                        Item Name
                     </label>
                     {errors[`${item.id}-name`] && <AlertCircle className="absolute right-4 top-3 w-4 h-4 text-red-400" />}
                </div>

                    {/* Price Row - Original Price */}
                    <div className="relative group/input">
                      <div className="absolute left-4 top-5 text-zinc-400 font-bold z-10 pointer-events-none">{currency}</div>
                         <input
                            type="number"
                            value={item.sellingPrice}
                            onChange={(e) => updateItem(item.id, 'sellingPrice', e.target.value)}
                            placeholder=" "
                            className={`
                                peer w-full bg-zinc-100/50 hover:bg-zinc-100 border-0 rounded-xl pl-14 pr-4 pt-5 pb-2 text-zinc-900 font-semibold text-base focus:ring-2 focus:ring-teal-500/20 focus:bg-white transition-all outline-none
                                ${errors[`${item.id}-sellingPrice`] ? 'ring-2 ring-red-100 bg-red-50' : ''}
                            `}
                         />
                         <label className="absolute left-14 top-2.5 text-[10px] font-bold text-teal-600 uppercase tracking-wider peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:text-zinc-500 peer-focus:top-2.5 peer-focus:text-[10px] peer-focus:text-teal-600 transition-all pointer-events-none">
                            Original Price
                         </label>
                    </div>

                    <div className="relative">
                        <textarea
                            rows={2}
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            placeholder=" "
                            className="peer w-full bg-zinc-100/50 hover:bg-zinc-100 border-0 rounded-xl px-4 pt-6 pb-2 text-zinc-700 font-medium focus:ring-2 focus:ring-teal-500/20 focus:bg-white transition-all outline-none resize-none"
                        />
                        <label className="absolute left-4 top-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:text-zinc-500 peer-focus:top-2.5 peer-focus:text-[10px] peer-focus:text-teal-500 transition-all pointer-events-none">
                            Description (Optional)
                        </label>
                    </div>

                    {/* Image & Options Grid */}
                    <div className="grid grid-cols-1">
                        <label className="relative cursor-pointer group/img block">
                             <input type="file" accept="image/*" onChange={(e) => handleFileChange(item.id, e)} className="hidden" />
                             <div className={`
                                h-full min-h-[120px] rounded-2xl border-2 border-dashed border-zinc-200 hover:border-teal-400 hover:bg-teal-50 transition-all flex flex-col items-center justify-center text-center p-0 overflow-hidden
                                ${item.image ? 'bg-zinc-50 border-zinc-300' : 'bg-zinc-50'}
                             `}>
                                {item.image || item.imagePreview ? (
                                    <div className="relative w-full h-full group/preview">
                                        <img src={item.imagePreview || item.image || ''} alt="Preview" className="w-full h-48 object-cover" />
                                        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center opacity-0 group-hover/preview:opacity-100 transition-opacity backdrop-blur-sm">
                                            <ImageIcon className="w-6 h-6 text-white mb-1" />
                                            <span className="text-xs font-bold text-white">Change Photo</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-6 flex flex-col items-center">
                                        <ImageIcon className="w-6 h-6 text-zinc-300 group-hover/img:text-teal-400 mb-2 transition-colors" />
                                        <span className="text-xs font-bold text-zinc-400 group-hover/img:text-teal-600">
                                            {uploadingId === item.id ? 'Uploading...' : 'Upload Photo (Optional)'}
                                        </span>
                                        {uploadError && uploadingId !== item.id && (
                                          <span className="text-[10px] text-red-500 font-semibold mt-1">{uploadError}</span>
                                        )}
                                    </div>
                                )}
                             </div>
                        </label>
                    </div>

                  </div>
                  
                  {/* Footer of Card */}
                  <div className="bg-zinc-50/80 px-6 py-3 border-t border-zinc-100 flex justify-center">
                        <button onClick={() => toggleExpand(item.id)} className="text-xs font-bold text-zinc-400 hover:text-zinc-600 uppercase tracking-widest flex items-center gap-1">
                            Close <ChevronUp className="w-3 h-3" />
                        </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-4 text-center text-sm text-zinc-500">
          <p className="font-bold text-zinc-700 mb-1">Menu selection coming soon</p>
          <p>Manual entry is active. Menu → Campaign mapping will be enabled in a later release.</p>
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          onClick={handleAddItem}
          className="group flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-zinc-200 shadow-sm text-zinc-600 font-bold hover:shadow-md hover:border-teal-200 hover:text-teal-600 transition-all"
        >
          <div className="bg-zinc-100 rounded-full p-1 group-hover:bg-teal-100 transition-colors">
            <Plus className="w-4 h-4" />
          </div>
          Add Another Item
        </button>
      </div>

      {!hideNav && (
        <div className="fixed bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-lg border-t border-zinc-200/60 z-40">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button 
                onClick={onBack}
                className="px-6 py-3 rounded-2xl text-zinc-600 font-bold hover:bg-zinc-100 transition-colors"
            >
                Back
            </button>
            
            <button
                onClick={validateAndProceed}
                className="flex items-center px-8 py-4 bg-zinc-900 text-white text-base font-bold rounded-full shadow-lg hover:bg-zinc-800 hover:scale-105 active:scale-95 transition-all shadow-zinc-900/20"
            >
                Review
                <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      {!hideNav && <div className="h-24" />}
    </div>
  );
};
